import {
  normalizeInstrumentSlug,
  normalizeLevelKey,
} from '@/lib/dashboardHomeSurvey';
import { URLS } from '@/lib/constants';
import type { MembershipTier } from '@/types/live-session';

type Metadata = Record<string, unknown> | null | undefined;

const PIANO_HOME_COURSE: Record<string, string> = {
  beginner: 'jazz-piano-foundations',
  intermediate: 'jazz-piano-expansions',
  advanced: 'jazz-piano-method',
};

export function instrumentDashboardPath(instrumentSlug: string): string {
  return `/courses/${instrumentSlug.trim().toLowerCase()}-dashboard`;
}

function readInstrument(metadata: Metadata): string | null {
  if (!metadata) return null;
  const candidate =
    metadata.instrument ??
    metadata.primary_instrument ??
    metadata.main_instrument;
  return typeof candidate === 'string' ? candidate.trim().toLowerCase() : null;
}

function readLevelRaw(metadata: Metadata): unknown {
  if (!metadata) return null;
  return metadata.level ?? metadata.user_level ?? metadata.skill_level;
}

export function resolveUserHomeUrl(
  metadata: Metadata,
  _membershipTier: MembershipTier,
  membershipLoading = false,
): string {
  if (membershipLoading) {
    return URLS.DASHBOARD;
  }

  if (!metadata) return URLS.DASHBOARD;

  const instrument = normalizeInstrumentSlug(readInstrument(metadata));
  if (!instrument) return URLS.DASHBOARD;

  if (instrument === 'piano') {
    const levelKey = normalizeLevelKey(readLevelRaw(metadata));
    const slug = levelKey ? PIANO_HOME_COURSE[levelKey] : null;
    return slug ? `/courses/${slug}` : URLS.DASHBOARD;
  }

  return instrumentDashboardPath(instrument);
}

export function dashboardSlugFromHomeUrl(homeUrl: string): string | null {
  const match = homeUrl.match(/^\/courses\/([^/?#]+)/);
  return match?.[1] ?? null;
}

/** Same routing logic as the header home icon — avoids instrument/level slug mismatches. */
export function isUserHomeCourseSlug(
  metadata: Metadata,
  membershipTier: MembershipTier,
  membershipLoading: boolean,
  courseSlug: string,
): boolean {
  const homeUrl = resolveUserHomeUrl(metadata, membershipTier, membershipLoading);
  return dashboardSlugFromHomeUrl(homeUrl) === courseSlug;
}
